import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useSocket } from "../../../providers/SocketProvider";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../../../store/auth/selectors";
import {
  useGetAllNotifications,
  useMarkNotificationAsSeen,
} from "../../../api/notifications";
import { getNotificationRedirectPath } from "../../../utils/notificationRedirect";
import toastr from "toastr";

//i18n
import { withTranslation } from "react-i18next";

const NotificationDropdown = (props) => {
  const [menu, setMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  const { socket, isConnected } = useSocket();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { mutate: fetchNotifications } = useGetAllNotifications();
  const { mutate: markNotificationAsSeen } = useMarkNotificationAsSeen();

  // Fetch initial notifications from API (first 5)
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchNotifications(
      { page: 1, limit: 5, search: undefined },
      {
        onSuccess: (response) => {
          const payload = response?.data ?? response;
          const apiNotifications = payload?.notifications ?? [];

          // Map API response to component format
          const mappedNotifications = apiNotifications.map((notif) => {
            // Determine redirect slug and reference based on notification type
            let redirectSlug = notif.redirectSlug;
            let reference = notif.reference;

            // If bookingRequestId exists, it's a booking request notification
            // Use the booking slug from invite.booking.slug for redirect
            if (notif.bookingRequestId) {
              if (notif.invite?.booking?.slug) {
                redirectSlug = notif.invite.booking.slug;
                reference = "booking";
              } else if (notif.invite?.slug) {
                // Fallback to booking-request slug if booking slug not available
                redirectSlug = notif.invite.slug;
                reference = "booking-request";
              }
            }
            // If booking exists but no bookingRequestId, it's a booking notification
            else if (notif.invite?.booking?.slug) {
              redirectSlug = notif.invite.booking.slug;
              reference = "booking";
            }
            // If userSlug exists, use it
            else if (notif.userSlug) {
                redirectSlug = notif.userSlug;

                // ✅ correct source of truth
                reference = notif.reference || notif.senderMode || notif.sender?.role;
            }

            return {
              id: notif.id,
              title: notif.title,
              message: notif.message,
              read: notif.seen || false,
              createdAt: notif.createdAt ? new Date(notif.createdAt) : new Date(),
              link: notif.link,
              type: notif.senderMode || "info",
              senderMode: notif.senderMode,
              redirectSlug: redirectSlug,
              reference: reference
            };
          });

          setNotifications(mappedNotifications);

          // Calculate unread count
          const unread = mappedNotifications.filter((n) => !n.read).length;
          setUnreadCount(unread);
        }
      }
    );
  }, [isAuthenticated, fetchNotifications]);

  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    // Listen for new notifications from socket
    const handleNewNotification = (notificationData) => {
      console.log("[NotificationDropdown] New notification received:", notificationData);

      // Map socket notification to component format
      // Determine redirect slug and reference based on notification type
      let redirectSlug = notificationData.redirectSlug;
      let reference = notificationData.reference;

      // If bookingRequestId exists, it's a booking request notification
      // Use the booking slug from invite.booking.slug for redirect
      if (notificationData.bookingRequestId) {
        if (notificationData.invite?.booking?.slug) {
          redirectSlug = notificationData.invite.booking.slug;
          reference = "booking";
        } else if (notificationData.invite?.slug) {
          // Fallback to booking-request slug if booking slug not available
          redirectSlug = notificationData.invite.slug;
          reference = "booking-request";
        }
      }
      // If booking exists but no bookingRequestId, it's a booking notification
      else if (notificationData.invite?.booking?.slug) {
        redirectSlug = notificationData.invite.booking.slug;
        reference = "booking";
      }
      // If userSlug exists, use it
      else if (notificationData.userSlug) {
        redirectSlug = notificationData.userSlug;
        // Determine reference from receiver role if available
        if (notificationData.receiver?.role) {
          reference = notificationData.receiver.role;
        }
      }

      const newNotification = {
        id: notificationData.id || Date.now(),
        title: notificationData.title,
        message: notificationData.message,
        read: notificationData.seen || notificationData.read || false,
        createdAt: notificationData.createdAt ? new Date(notificationData.createdAt) : new Date(),
        link: notificationData.link,
        type: notificationData.senderMode || "info",
        senderMode: notificationData.senderMode,
        redirectSlug: redirectSlug,
        reference: reference
      };

      // Show toast notification
      if (notificationData.title && notificationData.message) {
        // Configure toastr options for better appearance
        toastr.options = {
          closeButton: true,
          debug: false,
          newestOnTop: true,
          progressBar: true,
          positionClass: "toast-top-right",
          preventDuplicates: false,
          onclick: () => {
            // Mark notification as seen when toast is clicked
            if (notificationData.id && !notificationData.seen) {
              markNotificationAsSeen(notificationData.id);
            }
            
            // Navigate using reference and redirectSlug
            const redirectPath = getNotificationRedirectPath(
              notificationData.reference,
              notificationData.redirectSlug,
              notificationData.senderMode
            );
            if (redirectPath) {
              navigate(redirectPath);
            } else if (notificationData.link) {
              navigate(notificationData.link);
            } else {
              // Open notification dropdown
              setMenu(true);
            }
          },
          showDuration: "300",
          hideDuration: "1000",
          timeOut: "6000",
          extendedTimeOut: "1000",
          showEasing: "swing",
          hideEasing: "linear",
          showMethod: "fadeIn",
          hideMethod: "fadeOut",
          tapToDismiss: true
        };

        // Determine toast type
        let toastType = "info";

        if (notificationData.senderMode === "client") {
          toastType = "info";
        } else if (notificationData.senderMode === "worker") {
          toastType = "success";
        }

        // Format notification message with title and message
        const toastMessage = `<strong style="display: block; margin-bottom: 4px;">${notificationData.title}</strong>${notificationData.message}`;

        // Show toast
        toastr[toastType](toastMessage, "", {
          onclick: () => {
            // Mark notification as seen when toast is clicked
            if (notificationData.id && !notificationData.seen) {
              markNotificationAsSeen(notificationData.id);
            }
            
            // Navigate using reference and redirectSlug
            const redirectPath = getNotificationRedirectPath(
              notificationData.reference,
              notificationData.redirectSlug,
              notificationData.senderMode
            );
            if (redirectPath) {
              navigate(redirectPath);
            } else if (notificationData.link) {
              navigate(notificationData.link);
            } else {
              setMenu(true);
            }
          }
        });
      }

      // Add to beginning of list and limit to 5
      setNotifications((prev) => {
        const updated = [newNotification, ...prev].slice(0, 5);
        return updated;
      });

      // Update unread count
      setUnreadCount((prev) => prev + 1);
    };

    // Listen for notification event: "new-notification"
    socket.on("new-notification", handleNewNotification);

    return () => {
      socket.off("new-notification", handleNewNotification);
    };
  }, [socket, isAuthenticated, isConnected, navigate]);

  // Mark notification as read
  const markAsRead = (notificationId) => {
    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === notificationId ? { ...notif, read: true } : notif))
    );

    // Update unread count
    setUnreadCount((prev) => Math.max(0, prev - 1));

    // Call API to mark notification as seen
    markNotificationAsSeen(notificationId, {
      onError: () => {
        // Revert optimistic update on error
        setNotifications((prev) =>
          prev.map((notif) => (notif.id === notificationId ? { ...notif, read: false } : notif))
        );
        setUnreadCount((prev) => prev + 1);
      }
    });
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
    setUnreadCount(0);

    // Optional: Emit to server
    if (socket) {
      // socket.emit("mark_all_notifications_read");
    }
  };

  // Format time ago
  const getTimeAgo = (date) => {
    if (!date) return "";
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);

    if (diff < 60) return `${diff} ${props.t("sec ago") || "sec ago"}`;
    if (diff < 3600) return `${Math.floor(diff / 60)} ${props.t("min ago") || "min ago"}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${props.t("hours ago") || "hours ago"}`;
    return `${Math.floor(diff / 86400)} ${props.t("days ago") || "days ago"}`;
  };

  // Only show if authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className="bx bx-bell" />
          {unreadCount > 0 && (
            <span
              className="badge bg-danger rounded-pill position-absolute"
              style={{ top: "6px", right: "2px" }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg p-0 dropdown-menu-end">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0">{props.t("Notifications")}</h6>
              </Col>
              <div className="col-auto">
                {unreadCount > 0 && (
                  <button
                    className="btn btn-sm btn-link p-0 text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      markAllAsRead();
                    }}
                  >
                    {props.t("Mark all as read") || "Mark all as read"}
                  </button>
                )}
              </div>
            </Row>
          </div>

          <SimpleBar style={{ height: "300px", maxHeight: "300px" }}>
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="bx bx-bell-off fs-3 d-block mb-2" />
                <p className="mb-0">{props.t("No notifications") || "No notifications"}</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const redirectPath = getNotificationRedirectPath(
                  notification.reference,
                  notification.redirectSlug,
                  notification.senderMode
                );
                return (
                  <Link
                    key={notification.id}
                    to={redirectPath || notification.link || "#"}
                    className={`text-reset notification-item ${
                      !notification.read ? "notification-unread" : ""
                    }`}
                    onClick={() => markAsRead(notification.id)}
                    style={{
                      backgroundColor: !notification.read ? "#f8f9fa" : "transparent"
                    }}
                  >
                    <div className="d-flex p-3">
                      <div className="avatar-xs me-3 flex-shrink-0">
                        <span
                          className={`avatar-title rounded-circle font-size-16 ${
                            notification.type === "success"
                              ? "bg-success"
                              : notification.type === "warning"
                              ? "bg-warning"
                              : notification.type === "error"
                              ? "bg-danger"
                              : "bg-primary"
                          }`}
                        >
                          <i
                            className={
                              notification.icon ||
                              (notification.type === "success"
                                ? "bx bx-check-circle"
                                : notification.type === "warning"
                                ? "bx bx-error"
                                : notification.type === "error"
                                ? "bx bx-x-circle"
                                : "bx bx-info-circle")
                            }
                          />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1">
                          {notification.title || props.t("New notification")}
                          {!notification.read && (
                            <span className="badge bg-danger rounded-pill ms-2">New</span>
                          )}
                        </h6>
                        <div className="font-size-12 text-muted">
                          <p className="mb-1">{notification.message || notification.body || ""}</p>
                          <p className="mb-0">
                            <i className="mdi mdi-clock-outline" />{" "}
                            {getTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </SimpleBar>

          {notifications.length > 0 && (
            <div className="p-2 border-top d-grid">
              <Link
                className="btn btn-sm btn-link font-size-14 btn-block text-center"
                to="/notifications/list"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/notifications/list");
                }}
              >
                <i className="mdi mdi-arrow-right-circle me-1"></i>
                {props.t("View all") || "View all"}
              </Link>
            </div>
          )}
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default withTranslation()(NotificationDropdown);

NotificationDropdown.propTypes = {
  t: PropTypes.any
};
